import React, { useEffect, useState, useRef } from "react";
import { Input, Button, List } from "antd";
import { SendOutlined } from "@ant-design/icons";
// import CryptoJS from "crypto-js";
import { useDispatch, useSelector } from "react-redux";
import { appDispatch, RootState } from "../../../app/store";
import {
  getMessages,
  getPrivateKey,
  getPublicKey,
  sendEncryptedMessage,
  fetchBusinessAccountList,
} from "../../../features/members/membersSlice";
import { StorageService } from "../../../core/storageService/storageService";

/* ===================== COMPONENT ===================== */

interface Message {
  text?: string;
  encrypted_message?: string;
  encrypted_key?: string;
  iv?: string;
  fromMe: boolean;
}

const SAPBACommunicationNew = () => {
  const dispatch = useDispatch<appDispatch>();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const businessAccountList = useSelector(
    (state: RootState) =>
      state.members.fetchBusinessAccountList.fetchBusinessAccountListData
  );

  const apiMessages = useSelector(
    (state: RootState) => state.members.getMessages.getMessagesData
  );

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [publicKeyPem, setPublicKeyPem] = useState<string | null>(null);
  const [senderPublicKeyPem, setSenderPublicKeyPem] = useState<any | null>(null);

  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);

  const storedData = StorageService.getObjStorage("super_admin_user");
  const userObj =
  typeof storedData === "string" ? JSON.parse(storedData) : storedData;
  const userId: any = Number(userObj?.result?.user_data?.id);

  /* ---------- USER SELECT ---------- */
  const handleUserSelect = async (user: any) => {
  
    setSelectedUser(user);
    setMessages([]);

    const pub = await dispatch(getPublicKey({ id: user.id })).unwrap();


    setPublicKeyPem(pub.result.public_key);

    await dispatch(getMessages({ id: user.id }));
  };

  const ab2b64 = (buf: ArrayBuffer) =>
    btoa(String.fromCharCode(...new Uint8Array(buf)));

  const b642ab = (b64: string) =>
    Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer;


const importPrivateKey = async (pem: string): Promise<CryptoKey> =>
  crypto.subtle.importKey(
    "pkcs8",
    pemToArrayBuffer(pem),
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["decrypt"]
  );


const importPublicKey = (pem: string) =>
  crypto.subtle.importKey(
    "spki",
    pemToArrayBuffer(pem),   // ✅ correct
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"]
  );



  const generateAESKey = () =>
    crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );

  const encryptAES = async (text: string, key: CryptoKey) => {
    const iv: any = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(text)
    );

    return {
      encrypted_message: ab2b64(encrypted),
      iv: ab2b64(iv),
    };
  };

  const decryptAES = async (
    encrypted: string,
    iv: string,
    key: CryptoKey
  ) => {
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(b642ab(iv)) },
      key,
      b642ab(encrypted)
    );

    return new TextDecoder().decode(decrypted);
  };


  /* ---------- HELPER ---------- */
  const normalizePublicKey = (key: string) => {
    if (!key) throw new Error("Missing public key");
    key = key.replace(/\\n/g, "\n").trim();
    if (!key.includes("BEGIN PUBLIC KEY")) {
        throw new Error("Public key is not SPKI format");
    }
    return key;
  };

  const encryptAESKeyRSA = async (aesKey: CryptoKey, publicPem: string) => {
    const normalizedKey = normalizePublicKey(publicPem);
    const publicKey = await importPublicKey(normalizedKey);
    const rawKey = await crypto.subtle.exportKey("raw", aesKey);

const encrypted = await crypto.subtle.encrypt(
  { name: "RSA-OAEP" },   // ✅ NO hash here
  publicKey,
  rawKey
);

    return ab2b64(encrypted);
  };

  const decryptAESKeyRSA = async (
  encryptedKeyBase64: string,
  privateKey: CryptoKey
): Promise<CryptoKey> => {
const encryptedKeyBytes = base64ToUint8Array(encryptedKeyBase64);

const decryptedKeyBuffer = await crypto.subtle.decrypt(
  { name: "RSA-OAEP" },   // ✅ NO hash here
  privateKey,
  encryptedKeyBytes.buffer as ArrayBuffer
);


  return await crypto.subtle.importKey(
    "raw",
    decryptedKeyBuffer,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );
};

const pemToArrayBuffer = (pem: string): ArrayBuffer => {
  // 1. Extract the body content using a regex that finds the headers
  const pattern = /-----BEGIN [^-]+-----([\s\S]+?)-----END [^-]+-----/;
  const match = pem.match(pattern);
  
  let content = "";
  if (match) {
    content = match[1];
  } else {
    // Fallback
    content = pem
      .replace(/-----BEGIN [^-]+-----/, "")
      .replace(/-----END [^-]+-----/, "");
  }

  // 2. Fix common corruption: " " (space) instead of "+"
  content = content.replace(/ /g, "+");

  // 3. Remove all non-base64 characters
  const base64 = content.replace(/[^A-Za-z0-9+/=]/g, "");
  return b642ab(base64);
};



 const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
};

  useEffect(() => {
    dispatch(fetchBusinessAccountList({ searchquery: "" }));


  }, [dispatch]);


const decryptChatMessage = async (
  msg: any,
  loggedInUserId: number
) => {
  
  const privatePem = localStorage.getItem("private_key");
  if (!privatePem) return "[Missing private key]";

  let encryptedAesKey: string | undefined;
  
  if (Number(msg.receiver_id) === loggedInUserId) {
    encryptedAesKey = msg.encrypted_key;
  } else if (Number(msg.sender_id) === loggedInUserId) {
    encryptedAesKey = msg.sender_encrypted_key;
  }

  if (!encryptedAesKey) return "[No key available]";

  try {
    // ✅ IMPORT PRIVATE KEY
    const privateKey = await importPrivateKey(privatePem);

    // ✅ DECRYPT AES KEY
    const aesKey = await decryptAESKeyRSA(
      encryptedAesKey,
      privateKey
    );

    // ✅ DECRYPT MESSAGE
    return await decryptAES(
      msg.encrypted_message,
      msg.iv,
      aesKey
    );
  } catch (err) {
    console.error("Decryption failed", err);
    return "[Unable to decrypt]";
  }
};

  /* ---------- LOAD MESSAGES ---------- */
  useEffect(() => {
    if (!selectedUser) return;

    const run = async () => {
    
      const filtered = apiMessages.filter((m: any) => {
        const sender = Number(m.sender_id);
        const receiver = Number(m.receiver_id);
        return (
          (sender === userId && receiver === selectedUser.id) ||
          (sender === selectedUser.id && receiver === userId)
        );
      });

   const decrypted = await Promise.all(
  filtered.map(async (msg: any) => {
    try {
      const text = await decryptChatMessage(msg, userId);

      return {
        ...msg,
        text,
        fromMe: Number(msg.sender_id) === userId,
      };
    } catch (err) {
      console.error("Decrypt error", err);
      return {
        ...msg,
        text: "[Unable to decrypt]",
        fromMe: Number(msg.sender_id) === userId,
      };
    }
  })
);

      setMessages(decrypted);
    };

    run();
  }, [apiMessages, selectedUser]);

  const handleSend = async () => {
    if (!message.trim() || !publicKeyPem || !selectedUser) return;

    const plaintext = message;
    setMessage("");

    // optimistic UI
    setMessages((prev) => [...prev, { text: plaintext, fromMe: true }]);

    const aesKey = await generateAESKey();
    const { encrypted_message, iv } = await encryptAES(plaintext, aesKey);

    // 🔐 encrypt AES key using RECEIVER public key
    const encrypted_key = await encryptAESKeyRSA(aesKey, publicKeyPem);
    const senderPubKey = localStorage.getItem("public_key");
if (!senderPubKey) throw new Error("Sender public key missing");
    const sender_encrypted_key = await encryptAESKeyRSA(aesKey, senderPubKey);
    await dispatch(
      sendEncryptedMessage({
        receiver_id: selectedUser.id,
        encrypted_message,
        sender_encrypted_key,
        encrypted_key,
        iv,
      })
    ).unwrap();
  };

  /* ---------- UI ---------- */
  return (
    <div style={{ width: "100%", display: "flex", height: "100%", background: "#11161c" }}>
      <div style={{ width: 300, borderRight: "1px solid #21262c" }}>
        <div style={{ padding: 12, color: "#fff", fontWeight: "bold" }}>
          Users
        </div>
        <List
          dataSource={businessAccountList}
          renderItem={(item: any) => (
            <List.Item
              onClick={() => handleUserSelect(item)}
              style={{
                cursor: "pointer",
                color: "#fff",
                background:
                  selectedUser?.id === item.id ? "#21262c" : "transparent",
              }}
            >
              {item.name}
            </List.Item>
          )}
        />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            padding: 12,
            color: "#fff",
            fontWeight: "bold",
            borderBottom: "1px solid #21262c",
          }}
        >
          {selectedUser ? selectedUser.name : "Select User"}
        </div>

        <div style={{ flex: 1, padding: 12, overflowY: "auto" }}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: m.fromMe ? "flex-end" : "flex-start",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  background: m.fromMe ? "#4554fa" : "#21262c",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 16,
                  maxWidth: "70%",
                }}
              >
                {m.text}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div style={{ padding: 12 }}>
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onPressEnter={handleSend}
            placeholder="Type a message"
            suffix={
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SAPBACommunicationNew;